import Feature from '@/interfaces/geo/feature.interface';
import {Observable, of, Subject} from 'rxjs';
import {delay, map, mergeMap, take} from 'rxjs/operators';
import MapFeaturesService from '@/services/map-features.service';
import MammadabaMap from '@/interfaces/geo/mammadaba-map.interface';
import LayerVisibility from '@/interfaces/geo/layer-visibility.interface';
import MapLayersService from '@/services/map-layers.service';
import GameUserPosition from '@/interfaces/geo/game-user-position';
import {CustomObservable} from '@/library/observable/custom-observable';
import ForestGameSettings from '@/services/forest-game-settings';

declare const GeoMammadabaMap: MammadabaMap;

export default class GameMapService {
    public whenGameSelected: Subject<Feature> = new Subject();
    public whenGameLocationChanged: Subject<GameUserPosition> = new Subject();
    public whenGameCompleted: Subject<Feature> = new Subject();
    public whenCheckPointSelected: Subject<Feature> = new Subject();
    public whenCheckPointUnselected: Subject<void> = new Subject();
    private whenMapMoveEnd: Subject<void> = new Subject();
    private static instance: GameMapService;
    private mapFeaturesService: MapFeaturesService = MapFeaturesService.getInstance();
    private mapLayersService: MapLayersService = MapLayersService.getInstance();
    private mapMoveInAction: boolean = false;
    private lastZoomedFeature: Feature | null = null;
    private lastSelectedGameId: { gameId: string, timestamp: number } | null = null;
    private lastStartedGame: Feature | null = null;
    private lastFoundCheckPoint: Feature | null = null;
    private lastGameCompleted: Feature | null = null;
    private lastSelectedCheckPointId: { checkPointId: string, timestamp: number } | null = null;
    private localPadding: { left: number, duration: number } = {left: 0, duration: 850};
    private lastGameUserPosition: GameUserPosition | null = null;
    private approachedCheckPoints: Feature[] = [];
    private forestGameSettings: ForestGameSettings = ForestGameSettings.getInstance();

    private constructor() {
        this.subscribeOnEvents();
    }

    public static getInstance(): GameMapService {
        if (!GameMapService.instance) {
            GameMapService.instance = new GameMapService();
        }
        return GameMapService.instance;
    }

    public selectGame(gameId: string): Observable<void> {
        this.lastSelectedGameId = {gameId: gameId, timestamp: Date.now()};
        return this.renderMap();
    }

    public startGame(lvmGeoId: string): Observable<void> {
        return of(null).pipe(
            mergeMap(() => this.mapFeaturesService.gameFeatureForGameId(lvmGeoId)),
            map((feature: Feature) => this.lastStartedGame = feature),
            mergeMap(() => this.renderMap()),
        );
    }

    public foundCheckPoint(feature: Feature): Observable<void> {
        this.lastFoundCheckPoint = feature;
        return this.renderMap();
    }

    public selectCheckpoint(lvmGeoId: string): Observable<void> {
        this.lastSelectedCheckPointId = {checkPointId: lvmGeoId, timestamp: Date.now()};
        return this.renderMap();
    }

    public unselectCheckpoint(): Observable<void> {
        this.lastSelectedCheckPointId = null;
        return this.renderMap();
    }

    public completeGame(lvmGeoId: string): Observable<void> {
        return of(null).pipe(
            mergeMap(() => this.mapFeaturesService.gameFeatureForGameId(lvmGeoId)),
            map((feature: Feature) => this.lastGameCompleted = feature),
            mergeMap(() => this.renderMap()),
        );
    }

    public changePadding(paddingLeft: number): Observable<void> {
        this.localPadding.left = paddingLeft;
        return this.renderMap();
    }

    public isFeatureApproached(featureId: string): boolean {
        return this.approachedCheckPoints.some((checkPoint: Feature) => {
            return checkPoint.values_.POINTID.toString() === featureId;
        })
    }

    public renderMap(): Observable<void> {
        return of(null).pipe(
            mergeMap(() => {
                const currentGameLayers: Feature[][] = this.currentGameLayers();
                const layerVisibilities: LayerVisibility[] = this.mapLayersService.gameLayerVisibilities();
                GeoMammadabaMap.renderMap(
                    {
                        features: [],
                        layerVisibility: layerVisibilities,
                        allFeatures: [],
                        selectFeatureById: null,
                        zoomTo: this.lastZoomedFeature,
                        mapVisible: true,
                        padding: this.localPadding,
                        nearbyCircleVisible: false,
                        filterOptions: null,
                        onClickOnFeature: () => {},
                        onUnselect: () => {},
                        // ROUTES
                        routeLayers: [],
                        filteredRoutes: null,
                        selectRouteById: null,
                        onRouteSelect: () => {},
                        onRouteUnselect: () => {},
                        // GAME
                        gameLayers: currentGameLayers,
                        selectGameById: this.lastSelectedGameId,
                        selectGameCheckPointById: this.lastSelectedCheckPointId,
                        startGame: this.lastStartedGame,
                        gameCompleted: this.lastGameCompleted,
                        gameApproachingRadius: this.forestGameSettings.approachingRadius(),
                        gameLeavingRadius: this.forestGameSettings.leavingRadius(),
                        checkPointFound: this.lastFoundCheckPoint,
                        useGameSimulator: this.forestGameSettings.simulatorMode(),
                        onGameSelected: this.handleGameSelected.bind(this),
                        onGameLocationChange: this.handleGamePositionChange.bind(this),
                        onGameCompleted: this.handleGameCompleted.bind(this),
                        onCheckPointSelected: this.handleCheckPointSelected.bind(this),
                    },
                    this.mapElement()
                );
                return this.mapReadyForNextRender();
            })
        );
    }

    private handleGameSelected(feature: Feature | null): void {
        if (feature) {
            this.whenGameSelected.next(feature);
        }
    }

    private handleGamePositionChange(position: GameUserPosition): void {
        this.setApproachedCheckPoints(position);
        let positionChanged: boolean = true;
        if (this.lastGameUserPosition) {
            const location: number[] = this.lastGameUserPosition.location;
            positionChanged = location[0] !== position.location[0] || location[1] !== position.location[1];
        }
        if (positionChanged) {
            this.lastGameUserPosition = position;
            this.whenGameLocationChanged.next(this.lastGameUserPosition);
        }
    }

    private handleGameCompleted(feature: Feature | null): void {
        if (feature) {
            this.whenGameCompleted.next(feature);
        }
    }

    private handleCheckPointSelected(feature: Feature | null): void {
        if (feature) {
            this.lastSelectedCheckPointId && feature.values_.POINTID.toString() === this.lastSelectedCheckPointId.checkPointId
                ? this.selectCheckpointWithHackyWay(feature)
                : this.whenCheckPointSelected.next(feature);
        } else {
            this.whenCheckPointUnselected.next();
        }
    }

    private currentGameLayers(): Feature[][] {
        return this.lastSelectedGameId
            ? this.mapFeaturesService.allGameLayers().map((features: Feature[]) => {
                return features.filter((feature: Feature): boolean => {
                    return feature.values_.GAMEID.toString() === this.lastSelectedGameId!.gameId;
                });
            })
            : [];
    }

    private subscribeOnEvents(): void {
        document.addEventListener('movestart', (event) => {
            this.mapMoveInAction = true;
        });
        document.addEventListener('moveend', (event) => {
            this.mapMoveInAction = false;
            this.whenMapMoveEnd.next();
        });
    }

    /**
     * @description Hack, because selectGameCheckPointById in render map does not change icon to selected.
     */
    private selectCheckpointWithHackyWay(feature: Feature): void {
        this.mapReadyForNextRender().pipe(
            map(() => {
                feature.set('SELECTED', 1);
                feature.changed();
            })
        ).subscribe();
    }

    private setApproachedCheckPoints(position: GameUserPosition): void {
        if (position.approachingFeatures.length > 0 || position.leavingFeatures.length > 0) {
            const notApproachedCheckPoints: Feature[] = position.approachingFeatures.filter((feature: Feature) => {
                return !this.approachedCheckPoints.some((existingFeature: Feature) => {
                    return existingFeature.values_.POINTID === feature.values_.POINTID;
                });
            });
            this.approachedCheckPoints = this.approachedCheckPoints.concat(notApproachedCheckPoints);
            this.approachedCheckPoints = this.approachedCheckPoints.filter((existingFeature: Feature) => {
                return !position.leavingFeatures.some((checkFeature: Feature) => {
                    return checkFeature.values_.POINTID === existingFeature.values_.POINTID;
                });
            });
        }
    }

    private mapReadyForNextRender(): Observable<void> {
        return CustomObservable.empty().pipe(
            delay(50),
            mergeMap(() => {
                return this.mapMoveInAction
                    ? this.whenMapMoveEnd.pipe(take(1))
                    : CustomObservable.empty();
            }),
        );
    }

    private mapElement(): HTMLDivElement {
        return document.getElementById('map') as HTMLDivElement || new HTMLDivElement();
    }
}
